<template>
  <el-dialog
    :visible.sync="visible"
    :title="dialogTitle"
    width="960px"
    @close="handleClose"
  >
    <div class="toolbar">
      <el-input
        v-model="counterpartyUserId"
        size="mini"
        clearable
        placeholder="对手ID"
        style="width: 180px"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-input
        v-model="bizId"
        size="mini"
        clearable
        placeholder="业务ID"
        style="width: 180px"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-button size="mini" type="primary" @click="handleSearch">查询</el-button>
      <el-button size="mini" @click="handleExport">导出Excel</el-button>
    </div>

    <div class="summary-bar">
      <span>流入：<em class="amount-positive">{{ formatAmount(summary.addAmount) }}</em></span>
      <span>流出：<em class="amount-negative">{{ formatAmount(summary.subAmount) }}</em></span>
      <span>净额：<em :class="amountClass(summary.netAmount)">{{ formatAmount(summary.netAmount) }}</em></span>
    </div>

    <el-table :data="list" style="width: 100%" max-height="420">
      <el-table-column prop="createTime" label="时间" width="170">
        <template slot-scope="scope">
          {{ formatTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column prop="counterpartyUserId" label="对手ID" width="120">
        <template slot-scope="scope">
          {{ scope.row.counterpartyUserId == null ? '-' : scope.row.counterpartyUserId }}
        </template>
      </el-table-column>
      <el-table-column prop="bizId" label="业务ID" min-width="140" show-overflow-tooltip />
      <el-table-column prop="coin" label="币种" width="90" />
      <el-table-column prop="billTypeName" label="类型" min-width="140" show-overflow-tooltip />
      <el-table-column prop="side" label="方向" width="80">
        <template slot-scope="scope">
          <span :class="sideClass(scope.row.side)">{{ formatSide(scope.row.side) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="amount" label="金额" width="140">
        <template slot-scope="scope">
          <span :class="sideClass(scope.row.side)">{{ formatAmount(scope.row.amount) }}</span>
        </template>
      </el-table-column>
    </el-table>

    <pagination
      :key="paginationKey"
      :size="pagination.size"
      :page="pagination.page"
      :total="pagination.total"
      @pageChanged="handlePageChanged"
    />
  </el-dialog>
</template>

<script>
import Pagination from 'components/pagination';
import api from '../api';
import { formatPlainAmount } from '../../common/amount';

export default {
  name: 'BillStatisticDetailDialog',
  components: {
    Pagination,
  },
  data() {
    return {
      visible: false,
      query: null,
      summary: {
        billTypeName: '',
        coin: '',
        timeLabel: '',
        addAmount: 0,
        subAmount: 0,
        netAmount: 0,
      },
      list: [],
      pagination: {
        page: 1,
        size: 20,
        total: 0,
      },
      paginationKey: 0,
      counterpartyUserId: '',
      bizId: '',
    };
  },
  computed: {
    dialogTitle() {
      const parts = [this.summary.billTypeName || '流水明细'];
      if (this.summary.timeLabel) parts.push(this.summary.timeLabel);
      if (this.summary.coin) parts.push(this.summary.coin);
      return parts.join(' · ');
    },
  },
  methods: {
    open(query) {
      this.query = query;
      this.counterpartyUserId = '';
      this.bizId = '';
      this.pagination = { page: 1, size: 20, total: 0 };
      this.paginationKey += 1;
      this.visible = true;
      this.fetchDetail(1, 20);
    },
    handleClose() {
      this.visible = false;
      this.list = [];
      this.query = null;
      this.counterpartyUserId = '';
      this.bizId = '';
    },
    handleSearch() {
      this.pagination.page = 1;
      this.paginationKey += 1;
      this.fetchDetail(1, this.pagination.size);
    },
    parseCounterpartyUserId() {
      const raw = String(this.counterpartyUserId || '').trim();
      if (!raw) return '';
      if (!/^\d+$/.test(raw)) {
        this.$message.warning('对手ID须为数字');
        return null;
      }
      return raw;
    },
    fetchDetail(page = 1, size = 20) {
      if (!this.query || !this.query.billType) return;
      const counterpartyUserId = this.parseCounterpartyUserId();
      if (counterpartyUserId === null) return;
      const bizId = String(this.bizId || '').trim();
      api.detail({ ...this.query, counterpartyUserId, bizId, page, size }).then((res) => {
        if (res.code !== 0 || !res.data) return;
        const data = res.data;
        this.summary = {
          billTypeName: data.billTypeName,
          coin: data.coin || this.query.coin || '',
          timeLabel: data.timeLabel,
          addAmount: data.addAmount,
          subAmount: data.subAmount,
          netAmount: data.netAmount,
        };
        this.list = (data.page && data.page.list) || [];
        this.pagination.total = (data.page && data.page.totalCount) || 0;
        this.pagination.page = page;
        this.pagination.size = size;
      });
    },
    handleExport() {
      if (!this.query || !this.query.billType) return;
      const counterpartyUserId = this.parseCounterpartyUserId();
      if (counterpartyUserId === null) return;
      const bizId = String(this.bizId || '').trim();
      api.detailExcel({ ...this.query, counterpartyUserId, bizId }).then((res) => {
        const link = document.createElement('a');
        const blob = new Blob([res], { type: 'application/vnd.ms-excel' });
        link.style.display = 'none';
        link.href = URL.createObjectURL(blob);
        link.download = '公共账户流水明细';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      });
    },
    handlePageChanged({ page, size }) {
      this.fetchDetail(page, size);
    },
    formatAmount(value) {
      return formatPlainAmount(value);
    },
    formatTime(value) {
      if (!value) return '-';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    formatSide(side) {
      return side === 'ADD' ? '流入' : side === 'SUB' ? '流出' : side || '-';
    },
    amountClass(value) {
      return Number(value) >= 0 ? 'amount-positive' : 'amount-negative';
    },
    sideClass(side) {
      return side === 'ADD' ? 'amount-positive' : 'amount-negative';
    },
  },
};
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.summary-bar {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  font-size: 14px;
}

.summary-bar em {
  font-style: normal;
  font-weight: 600;
}

.amount-positive {
  color: #67c23a;
  cursor: pointer;
}

.amount-negative {
  color: #f56c6c;
  cursor: pointer;
}
</style>
